import Configuration from "config";

type CookiebotConsent = NonNullable<Window["Cookiebot"]>["consent"];
type ConsentCategory = Exclude<keyof CookiebotConsent, "necessary" | "stamp">;

export const isCookieConsentEnabled = () => Boolean(Configuration.COOKIEBOT_CBID);

// Reopens the consent banner so the user can change a previous choice.
export function renewCookieConsent() {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Cookiebot is a third-party global that may not have loaded
  window.Cookiebot?.renew?.();
}

// Fired by Cookiebot on page load once the stored consent state is known, and
// again whenever the user submits or changes their consent in the banner.
const CONSENT_EVENTS = [
  "CookiebotOnConsentReady",
  "CookiebotOnAccept",
  "CookiebotOnDecline",
];

/**
 * Loads the Cookiebot consent banner. Blocking mode is "manual" because the
 * app's trackers are npm-bundled SDKs initialized from code — Cookiebot's
 * automatic mode only intercepts script tags and cannot block them. Each
 * tracker must instead be gated through `bindConsent`.
 */
export function initCookieConsent() {
  if (!Configuration.COOKIEBOT_CBID) {
    return;
  }

  const script = document.createElement("script");
  script.id = "Cookiebot";
  script.src = "https://consent.cookiebot.com/uc.js";
  script.dataset.cbid = Configuration.COOKIEBOT_CBID;
  script.dataset.blockingmode = "manual";
  // Disable the floating "Privacy trigger" icon for this installation only
  // (the domain group setting still applies to the website); consent is
  // reopened via the footer's cookie-preferences link instead.
  script.dataset.widgetEnabled = "false";
  document.head.appendChild(script);
}

/**
 * Calls `onGranted`/`onRevoked` to match the user's consent for `category`,
 * now and whenever it changes. Handlers must be idempotent — they can fire
 * multiple times. Builds without a Cookiebot domain configured (local/dev)
 * assume consent is granted.
 */
export function bindConsent(
  category: ConsentCategory,
  onGranted: () => void,
  onRevoked?: () => void
) {
  if (!Configuration.COOKIEBOT_CBID) {
    onGranted();
    return;
  }

  const sync = () => {
    const consent = window.Cookiebot?.consent;
    if (!consent) {
      return;
    }

    if (consent[category]) {
      onGranted();
    } else {
      onRevoked?.();
    }
  };

  for (const event of CONSENT_EVENTS) {
    window.addEventListener(event, sync);
  }

  sync();
}
